import { NavLink, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeft } from 'lucide-react';
import { NAV, visibleSections } from './nav';
import { useVocab } from '../../lib/useBranding';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { useCanView } from '../../lib/AuthContext';
import { BrandMark } from './BrandMark';

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen = false,
  onMobileClose,
}: {
  collapsed: boolean;
  onToggle: () => void;
  /** Step 141 — drawer state on small screens; ignored at md and above. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const location = useLocation();
  const canView = useCanView();
  const sections = visibleSections(NAV, canView);
  /**
   * Step 79 — the tenant's own words.
   *
   * Resolved HERE rather than baked into `NAV`, because the nav tree is a
   * module-level constant and the vocabulary arrives from a query. One
   * lookup covers every item that named a noun.
   */
  const t = useVocab();
  const labelOf = (item: (typeof sections)[number]['items'][number]) =>
    item.vocabKey
      ? item.vocabPlural === false
        ? t.title(item.vocabKey)
        : t.titleMany(item.vocabKey)
      : item.label;

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={onMobileClose} aria-hidden />
      )}
      <aside
        className={cn(
          'flex h-full shrink-0 flex-col border-r bg-card transition-[width] duration-200',
          // Step 141 — hidden on phones unless opened as a drawer; a fixed rail from md.
          'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:shadow-xl',
          mobileOpen ? 'max-md:flex' : 'max-md:hidden',
          'md:flex',
          collapsed ? 'w-14' : 'w-60',
        )}
      >
        <div className={cn('flex h-14 items-center border-b px-3', collapsed ? 'justify-center' : 'gap-2')}>
          <BrandMark collapsed={collapsed} />
        </div>

        {/* Step 42 — the saved views have MOVED OUT of this sidebar.
          Kai: *"just having a ticket list on the side bar, then a secondary
          side bar with all the custom views"*, matching the reference, whose
          view rail lives inside the tickets page.
          The reason is scale rather than taste: this list was every view, so
          at Kai's thirty-five it would have pushed Catalogue, Articles,
          Reports and Settings off the screen — a nav where the primary
          destinations are below the fold is not a nav. Views now sit in their
          own rail next to the list they filter, which is also where somebody
          is when they want to switch. */}
        <nav className="flex-1 overflow-y-auto py-2">
          {sections.map((section, si) => (
            <div key={si} className="px-2 pb-2">
              {section.heading && !collapsed && (
                <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.heading}
                </p>
              )}
              {section.items.map((item) => {
                // NavLink's own isActive can't see query strings; a view link and
                // the bare /tickets link must not both light up.
                const active =
                  location.pathname + location.search === item.to ||
                  (item.to === location.pathname && !location.search && !item.to.includes('?'));
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={collapsed ? labelOf(item) : undefined}
                    className={cn(
                      'mb-0.5 flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      collapsed && 'justify-center px-0 py-2',
                      active
                        ? 'bg-accent font-medium text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="truncate">{labelOf(item)}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="icon"
            className="w-full"
            onClick={onToggle}
            aria-label="Toggle sidebar"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
      </aside>
    </>
  );
}
